"use client";

import type { CashFlowRow } from "@/lib/types/financial-snapshot";
import { axisLabel, niceScale, CHART_PADDING, CHART_HEIGHT, YAxis, XAxisLabels, ChartSvg } from "../ChartAxes";
import { useChartHover } from "../hover/use-chart-hover";
import { HoverHitAreas } from "../hover/HoverHitAreas";
import { HoverCrosshair } from "../hover/HoverCrosshair";
import { ChartTooltip } from "../hover/ChartTooltip";
import type { HoverPoint } from "../hover/types";

const VIEW_WIDTH = 640;
const BAR_GAP = 6;

const fmtMoney = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toFixed(0)}`;
};

export function CashFlowChart({ rows }: { rows: CashFlowRow[] }) {
  const { containerRef, activeIndex, handlePointerMove, handlePointerLeave, handlePointerUp } = useChartHover();

  const usable = rows.filter((r) => r.ocf != null || r.fcf != null);
  if (usable.length === 0) return null;

  const allVals = usable.flatMap((r) => [r.ocf, r.fcf].filter((v): v is number => v != null));
  const maxVal = Math.max(...allVals, 0);
  const minVal = Math.min(...allVals, 0);
  const scale = niceScale(maxVal, minVal);

  const plotTop = CHART_PADDING.top;
  const plotBottom = CHART_HEIGHT - CHART_PADDING.bottom;
  const plotHeight = plotBottom - plotTop;
  const zeroY = plotBottom - ((0 - scale.min) / (scale.max - scale.min)) * plotHeight;
  const yScale = (v: number) => plotBottom - ((v - scale.min) / (scale.max - scale.min)) * plotHeight;

  const groupWidth = (VIEW_WIDTH - CHART_PADDING.left - CHART_PADDING.right) / usable.length;
  const barWidth = Math.max(6, groupWidth / 2 - BAR_GAP);
  const xScale = (i: number) => CHART_PADDING.left + i * groupWidth + groupWidth / 2;

  const anchors = usable.map((_, i) => xScale(i));

  // Tooltip color dots match the rendered bar colors: OCF is green, FCF is blue.
  const points: HoverPoint[] = usable.map((row) => ({
    period: row.period,
    series: [
      { label: "Operating CF", value: row.ocf, format: fmtMoney, colorClass: "bg-cgreen" },
      { label: "Free CF", value: row.fcf, format: fmtMoney, colorClass: "bg-blue" },
    ],
  }));

  return (
    <div ref={containerRef} className="relative">
      <ChartSvg viewBox={`0 0 ${VIEW_WIDTH} ${CHART_HEIGHT}`}>
        <YAxis ticks={scale.ticks} scale={yScale} format={axisLabel} width={VIEW_WIDTH} />
        {usable.map((row, i) => {
          const groupX = xScale(i);
          const ocfX = groupX - BAR_GAP / 2 - barWidth;
          const fcfX = groupX + BAR_GAP / 2;
          return (
            <g key={row.period}>
              {row.ocf != null && (
                <rect x={ocfX} y={Math.min(yScale(row.ocf), zeroY)} width={barWidth} height={Math.abs(yScale(row.ocf) - zeroY)} className="fill-cgreen" rx={2} />
              )}
              {row.fcf != null && (
                <rect x={fcfX} y={Math.min(yScale(row.fcf), zeroY)} width={barWidth} height={Math.abs(yScale(row.fcf) - zeroY)} className={row.fcf >= 0 ? "fill-blue" : "fill-cred"} rx={2} />
              )}
            </g>
          );
        })}
        <XAxisLabels labels={usable.map((r) => r.period)} xScale={xScale} y={CHART_HEIGHT - 8} />

        <g transform={`translate(${CHART_PADDING.left}, 4)`}>
          <rect width={8} height={8} className="fill-cgreen" />
          <text x={12} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>OCF</text>
          <rect x={60} width={8} height={8} className="fill-blue" />
          <text x={72} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>FCF</text>
        </g>

        {activeIndex != null && <HoverCrosshair x={anchors[activeIndex]} plotTop={plotTop} plotBottom={plotBottom} />}
        <HoverHitAreas
          anchors={anchors}
          viewBoxWidth={VIEW_WIDTH}
          plotTop={plotTop}
          plotBottom={plotBottom}
          onActivate={handlePointerMove}
          onLeave={handlePointerLeave}
          onTap={handlePointerUp}
        />
      </ChartSvg>
      {activeIndex != null && (
        <ChartTooltip point={points[activeIndex]} anchorPct={(anchors[activeIndex] / VIEW_WIDTH) * 100} />
      )}
    </div>
  );
}
